import { z } from "zod";
import {
  DEFAULT_LATE_REPLY_THRESHOLDS,
  type LateReplyCause,
  LateReplyCauseSchema,
  lateReplyStage,
} from "../../../skills/leadcue-reply/scripts/lib/late-reply.ts";

/**
 * A reply that waited before drafting, as the inbox row and the draft show it: held while its
 * campaign was set up (Dawid, about 10:24 CEST on 2026-09-29), held while drafting was off, or found
 * late in Bison (decision 8d484c62, 2026-10-14). Only such a wait earns this; a reply waiting on the
 * rep is an ordinary wait and has none.
 */
export const LateReplyViewSchema = z.object({
  /** When the lead's message arrived: the age is counted from here, never from the re-queue. */
  receivedAt: z.string(),
  heldBy: LateReplyCauseSchema,
  /** When intake filed it as held. Null for a row filed before that time was kept. */
  heldAt: z.string().nullable(),
  releasedAt: z.string(),
  /** The workspace's saved late-reply thresholds; null where it saved none and the defaults hold. */
  delayLineAfterWorkingDays: z.number().int().nullable().default(null),
  checkInAfterDays: z.number().int().nullable().default(null),
  /** When the app last wrote a draft for this reply; null until it has. */
  draftedAt: z.string().nullable().default(null),
});
export type LateReplyView = z.infer<typeof LateReplyViewSchema>;

/**
 * Whether the draft the app wrote allows for the delay: the late-reply rule's own stage when the draft
 * was written, with the workspace's thresholds (`lateReplyStage`). Only then does the draft open with
 * the delay line or become a check-in; a reply held five minutes, or drafted inside the working-day
 * threshold, is drafted as if it were on time, and nothing may say otherwise.
 */
export function draftAllowsForDelay(view: LateReplyView): boolean {
  if (view.draftedAt === null) return false;
  const thresholds = {
    delayLineAfterWorkingDays:
      view.delayLineAfterWorkingDays ?? DEFAULT_LATE_REPLY_THRESHOLDS.delayLineAfterWorkingDays,
    checkInAfterDays: view.checkInAfterDays ?? DEFAULT_LATE_REPLY_THRESHOLDS.checkInAfterDays,
  };
  return lateReplyStage(thresholds, view.receivedAt, view.draftedAt) !== "on_time";
}

const DAY_MS = 86_400_000;

/** How long ago the lead wrote, in words a rep reads at a glance. */
export function replyAge(receivedAt: string, now: Date): string {
  const days = Math.floor((now.getTime() - Date.parse(receivedAt)) / DAY_MS);
  if (!Number.isFinite(days) || days < 1) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

/**
 * The row's words. The row's clock already says how long the lead has waited, so the row adds only
 * why; without it "Unanswered 5 d" reads as the rep's neglect. "Was off" because drafting is back on by
 * then, and the "Drafting off" chip names a switch that is off now.
 */
export const LATE_REPLY_ROW_LABEL = "Held during setup";

const SETUP = {
  label: LATE_REPLY_ROW_LABEL,
  why: "held this reply while the campaign was being set up",
};
/** What held the reply, as the row's label and its title say it (impeccable clarify, w553). */
const WAITED: Record<LateReplyCause, { label: string; why: string }> = {
  copy_check: SETUP,
  released_before_copy_read: SETUP,
  campaign_setup: SETUP,
  drafting_off: {
    label: "Held while drafting was off",
    why: "held this reply while drafting was switched off",
  },
  found_late: {
    label: "Found late in Bison",
    why: "found this reply in Bison more than a day after it arrived",
  },
};

/** The row's label: why the lead's message waited. */
export function lateReplyRowLabel(view: Pick<LateReplyView, "heldBy">): string {
  return WAITED[view.heldBy].label;
}

/**
 * The sentences behind the row's label, for its title. Says the draft is written for the delay only
 * where it is (`draftAllowsForDelay`); a short hold still says it was held.
 */
export function lateReplyExplanation(view: LateReplyView, now: Date): string {
  const wrote = replyAge(view.receivedAt, now);
  const why = WAITED[view.heldBy].why;
  return draftAllowsForDelay(view)
    ? `The lead wrote ${wrote}. LeadCue ${why}, so the draft is written for that delay.`
    : `The lead wrote ${wrote}. LeadCue ${why}.`;
}
